const PRODUCT_PROMISE_SELECTOR = 'pdqproductpromise';
const ADDRESS_INPUT_SELECTOR = 'pdq-pdp__autocomplete-input';
const observedPromiseElements = new WeakSet();
let reservedPromiseHeight = 0;
let lastRenderedVariantId = null;
let pendingVariantId = null;
let preservedAddressValue = null;
let restorePending = false;

function getPromiseSpaceTarget(element) {
  return element.closest('.shopify-app-block') ?? element;
}

function reserveProductPromiseSpace(element) {
  if (!(element instanceof HTMLElement)) return;

  const target = getPromiseSpaceTarget(element);
  const promise = target.matches(PRODUCT_PROMISE_SELECTOR) ? target : target.querySelector(PRODUCT_PROMISE_SELECTOR);
  const hasRenderedContent = Boolean(promise?.textContent?.trim() || promise?.querySelector('input, button'));
  const height = Math.ceil(target.getBoundingClientRect().height);

  // Do not measure PDQ's empty/loading state as the permanent height. The
  // first stable widget render establishes the reservation instead.
  if (hasRenderedContent && height > reservedPromiseHeight) {
    reservedPromiseHeight = height;
  }

  // Apply the shared reservation to newly-rendered app blocks too. Combined
  // listing morphs can replace the wrapper element before PDQ mounts again.
  if (reservedPromiseHeight > 0) {
    target.style.minHeight = `${reservedPromiseHeight}px`;
    target.dataset.pdqSpaceReserved = 'true';
  }
}

const promiseResizeObserver =
  typeof ResizeObserver === 'function'
    ? new ResizeObserver((entries) => {
        for (const entry of entries) {
          reserveProductPromiseSpace(entry.target);
        }
      })
    : null;

function observeProductPromise(element) {
  if (!(element instanceof HTMLElement)) return;

  const target = getPromiseSpaceTarget(element);
  if (observedPromiseElements.has(target)) {
    reserveProductPromiseSpace(target);
    return;
  }

  observedPromiseElements.add(target);
  reserveProductPromiseSpace(target);
  promiseResizeObserver?.observe(target);
}

function observeProductPromiseNodes(node) {
  if (!(node instanceof Element)) return;

  if (node.matches(PRODUCT_PROMISE_SELECTOR)) observeProductPromise(node);
  node.querySelectorAll(PRODUCT_PROMISE_SELECTOR).forEach(observeProductPromise);

  // The app-block wrapper can be inserted before the custom element mounts.
  if (node.matches('.shopify-app-block') && node.querySelector(PRODUCT_PROMISE_SELECTOR)) {
    observeProductPromise(node.querySelector(PRODUCT_PROMISE_SELECTOR));
  }
}

function setupProductPromiseSpaceReservation() {
  document.querySelectorAll(PRODUCT_PROMISE_SELECTOR).forEach(observeProductPromise);
  document.querySelectorAll('.shopify-app-block').forEach((element) => {
    if (element.querySelector(PRODUCT_PROMISE_SELECTOR))
      observeProductPromise(element.querySelector(PRODUCT_PROMISE_SELECTOR));
  });

  const root = document.documentElement;
  if (!root) return;

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(observeProductPromiseNodes);
    }
  }).observe(root, { childList: true, subtree: true });
}

function getAddressInput() {
  return document.querySelector(`.${ADDRESS_INPUT_SELECTOR}`);
}

function preserveAddressValue() {
  const input = getAddressInput();
  preservedAddressValue = input?.value ?? null;
  restorePending = Boolean(preservedAddressValue);
}

function restoreAddressValue() {
  if (!restorePending) return;

  const input = getAddressInput();
  if (!input) return;

  if (!preservedAddressValue || input.value === preservedAddressValue) {
    restorePending = false;
    return;
  }

  if (document.activeElement === input) {
    restorePending = false;
    preservedAddressValue = null;
    return;
  }

  const descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
  if (descriptor?.set) {
    descriptor.set.call(input, preservedAddressValue);
  } else {
    input.value = preservedAddressValue;
  }

  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
  restorePending = false;
  preservedAddressValue = null;
}

function reRenderProductPromiseForVariant(variantId) {
  const input = getAddressInput();
  if (input && document.activeElement === input) {
    pendingVariantId = String(variantId);
    return;
  }

  preserveAddressValue();

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      restoreAddressValue();
    });
  });

  window.PDQPluginsSettings?.reRenderProductPromise?.(String(variantId));
  lastRenderedVariantId = String(variantId);
  pendingVariantId = null;
}

function refreshDeliveryPromiseBlocks(event) {
  if (!(event.target instanceof Element)) return;
  if (event.target.closest('quick-add-dialog')) return;

  const picker = event.target.closest('variant-picker');
  if (!picker || picker.dataset.templateProductMatch !== 'true') return;

  const variantId = event.detail?.resource?.id;
  if (!variantId || String(variantId) === lastRenderedVariantId) return;

  reRenderProductPromiseForVariant(variantId);
}

function setupAddressPreservation() {
  document.addEventListener(
    'focus',
    (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (!event.target.classList.contains(ADDRESS_INPUT_SELECTOR)) return;
      restorePending = false;
      preservedAddressValue = null;
    },
    true
  );

  document.addEventListener(
    'blur',
    (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (!event.target.classList.contains(ADDRESS_INPUT_SELECTOR)) return;
      if (pendingVariantId) reRenderProductPromiseForVariant(pendingVariantId);
    },
    true
  );

  const root = document.documentElement;
  if (!root) return;

  new MutationObserver(() => {
    restoreAddressValue();
  }).observe(root, { childList: true, subtree: true });
}

setupProductPromiseSpaceReservation();
setupAddressPreservation();
document.addEventListener('variant:update', refreshDeliveryPromiseBlocks);
